<template>
  <v-dialog
    :value="dialog"
    :max-width="500"
    content-class="d-dialog"
    scrollable
    @keydown.esc="closeDialog"
    @click:outside="closeDialog"
  >
    <v-card outlined class="d-dialog-card">
      <v-card-title>
        <template v-if="isLoading">
          {{ $t('vehicle.checkOwnersData.titleChecking') }}
        </template>
        <template v-if="!isLoading && hasNewOwnersData">
          {{ $t('vehicle.checkOwnersData.titleHasNewOwnersData') }}
        </template>
        <template v-if="!isLoading && !hasNewOwnersData">
          {{ $t('vehicle.checkOwnersData.titleNoNewOwnersData') }}
        </template>

        <d-btn-icon
          type="close"
          btn-class="d-dialog-btn-close"
          tabindex="4"
          @click="closeDialog"
        ></d-btn-icon>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <div v-if="!isLoading">
          <div v-if="hasNewOwnersData">
            <div class="d-fieldset">
              <v-icon class="d-fieldset-icon">mdi-car</v-icon>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.trim="newOwnersData.licenseNumber"
                    :label="$t('vehicle.edit.licenseNumber')"
                    tabindex="1"
                    readonly
                    :disabled="!newOwnersData.licenseNumber"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.trim="newOwnersData.vinNumber"
                    :label="$t('vehicle.edit.vinNumber')"
                    tabindex="1"
                    readonly
                    :disabled="!newOwnersData.vinNumber"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <d-date-picker
                    v-model="newOwnersData.registrationDate"
                    :label="$t('vehicle.edit.registrationDate')"
                    tabindex="1"
                    readonly
                    :disabled="!newOwnersData.registrationDate"
                  ></d-date-picker>
                </v-col>
              </v-row>
            </div>
            <div class="d-fieldset">
              <v-icon class="d-fieldset-icon">mdi-account</v-icon>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.trim="newOwnersData.firstName"
                    :label="$t('vehicle.edit.firstName')"
                    tabindex="1"
                    readonly
                    :disabled="!newOwnersData.firstName"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.trim="newOwnersData.lastName"
                    :label="$t('vehicle.edit.lastName')"
                    tabindex="1"
                    readonly
                    :disabled="!newOwnersData.lastName"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newOwnersData.phone"
                    default-country="NO"
                    :label="$t('vehicle.edit.phone')"
                    tabindex="1"
                    readonly
                    :disabled="!newOwnersData.phone"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.trim="newOwnersData.emailAddress"
                    :label="$t('vehicle.edit.email')"
                    type="email"
                    tabindex="1"
                    readonly
                    :disabled="!newOwnersData.emailAddress"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>

            <div class="d-fieldset">
              <v-icon class="d-fieldset-icon">mdi-map-marker</v-icon>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model.trim="newOwnersData.address"
                    :label="$t('vehicle.edit.address')"
                    tabindex="1"
                    readonly
                    :disabled="!newOwnersData.address"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.trim="newOwnersData.zipCode"
                    :label="$t('vehicle.edit.zipCode')"
                    tabindex="1"
                    readonly
                    :disabled="!newOwnersData.zipCode"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.trim="newOwnersData.area"
                    :label="$t('vehicle.edit.area')"
                    tabindex="1"
                    readonly
                    :disabled="!newOwnersData.area"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>


          </div>
        </div>
      </v-card-text>

      <v-card-actions class="justify-center">
        <v-btn
          v-text="$t('cancel')"
          color="secondary"
          text
          tabindex="3"
          @click="closeDialog"
        ></v-btn>
        <v-btn
          :disabled="!hasNewOwnersData"
          v-text="$t('vehicle.checkOwnersData.btnUpdateOwner')"
          color="primary"
          text
          tabindex="2"
          @click="submit"
        ></v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script>
import { mapActions } from 'vuex'

import dialogMixin from '@/mixins/dialog.mixin'
import snackbarMixin from '@/mixins/snackbar.mixin'
import errorMixin from '@/mixins/error.mixin'
import DBtnIcon from '@/components/DBtnIcon'
import DDatePicker from '@/components/DDatePicker'

export default {
  props: {
    editedVehicle: Object
  },

  components: {
    DBtnIcon,
    DDatePicker
  },

  mixins: [
    dialogMixin,
    snackbarMixin,
    errorMixin
  ],


  data () {
    return {
      newOwnersData: {},
      isLoading: true,
      hasNewOwnersData: false
    }
  },

  methods: {
    ...mapActions('vehicle', ['getVehicleOwnerByKeyword']),

    async loadOwnersData () {
      this.isLoading = true
      this.hasNewOwnersData = false
      this.newOwnersData = {}
      const keyword = this.editedVehicle.licenseNumber || this.editedVehicle.vinNumber
      if (keyword) {
        const ownersData = await this.getVehicleOwnerByKeyword(keyword)
        this.isLoading = false
        if (ownersData) {
          Object.keys(ownersData).forEach(key => {
            const currentValue = this.editedVehicle[key]
            const newValue = ownersData[key]

            let isDifferent = false

            if (typeof newValue === 'string' && typeof currentValue === 'string') {
              isDifferent = newValue.trim().toLowerCase() !== currentValue.trim().toLowerCase()
            } else {
              isDifferent = newValue !== currentValue
            }

            if (newValue && isDifferent) {
              if (!this.hasNewOwnersData) this.hasNewOwnersData = true
              this.newOwnersData[key] = newValue
            }
          })
        }
      }
    },


    async onDialogOpen () {
      await this.loadOwnersData()
    },

    async submit () {
      this.$emit('update', this.newOwnersData)
    }
  }
}
</script>
